import { gql } from "@apollo/client";
import { useRouter } from "next/router";
import { FC, useRef, useState } from "react";

import useUser from "@src/features/app/hooks/useUser";
import SelectUsersDialog from "@src/features/contentLibrary/assignmentDialogs/SelectUsersDialog";
import { useToast } from "@src/lib/hooks/useToast";
import {
  ChatType,
  GraphqlOperations,
  UserSelectionInput,
} from "@src/types.generated";

import GroupChatDialog from "./GroupChatDialog";
import { useCreateChatMutation } from "./NewChatFlow.generated";
import { isSingleUserSelection } from "./utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewChatFlow: FC<Props> = ({ open, onOpenChange }) => {
  const router = useRouter();
  const { user } = useUser();
  const { addErrorToast } = useToast();
  const [selection, setSelection] = useState<UserSelectionInput | null>(null);
  const [groupOpen, setGroupOpen] = useState(false);
  const advancingToGroupRef = useRef(false);

  const [createChat, { loading }] = useCreateChatMutation({
    refetchQueries: [GraphqlOperations.Query.NewMessagingChats],
    onError: (error) => {
      addErrorToast({ ...error, callsite: "create_chat" });
    },
    onCompleted: (data) => {
      if (!data.createChat.success || !data.createChat.chat) {
        addErrorToast({ data, callsite: "create_chat" });
        return;
      }
      onOpenChange(false);
      router.push({
        pathname: "/messages/my-chats/[chatId]",
        query: { chatId: data.createChat.chat.id },
      });
    },
  });

  const handleSelect = (selected: UserSelectionInput) => {
    if (isSingleUserSelection(selected, user?.id)) {
      createChat({
        variables: {
          input: {
            userSelectionSet: { userSelections: [selected] },
            chatType: ChatType.OneOnOne,
          },
        },
      });
      return;
    }
    advancingToGroupRef.current = true;
    setSelection(selected);
    setGroupOpen(true);
  };

  const handlePickerOpenChange = (next: boolean) => {
    if (!next && advancingToGroupRef.current) {
      advancingToGroupRef.current = false;
      return;
    }
    onOpenChange(next);
  };

  const handleCreateGroup = (args: {
    title?: string;
    membersCanEditTitle: boolean;
    membersCanEditMembers: boolean;
    ownerUserIds: number[];
  }) => {
    if (!selection) return;
    createChat({
      variables: {
        input: {
          title: args.title ? { en: args.title } : undefined,
          userSelectionSet: { userSelections: [selection] },
          membersCanEditTitle: args.membersCanEditTitle,
          membersCanEditMembers: args.membersCanEditMembers,
          ownerUserIds: args.ownerUserIds,
          chatType: ChatType.Group,
        },
      },
    });
  };

  if (groupOpen && selection) {
    return (
      <GroupChatDialog
        open={groupOpen}
        onOpenChange={(o) => !o && onOpenChange(false)}
        selection={selection}
        loading={loading}
        onCreate={handleCreateGroup}
      />
    );
  }

  return (
    <SelectUsersDialog
      open={open}
      onOpenChange={handlePickerOpenChange}
      title="New Chat"
      pillColor="purple"
      requireSelection
      showUserTypeOptions
      defaultFilter="people"
      onSelect={handleSelect}
    />
  );
};

gql`
  mutation CreateChat($input: CreateChatInput!) {
    createChat(input: $input) {
      success
      error {
        code
      }
      chat {
        ...ChatListItem
      }
    }
  }
`;

export default NewChatFlow;
